// LeadOriginCard.tsx — "Where it came from" on the lead details page.
// Shows the fixed origin fields (channel, campaign, form, submitted time,
// sheet), then every extra attribute and custom field — whatever came in,
// including columns Meta adds in the future.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, ClipboardList, Compass, ListPlus, LucideIcon, Megaphone, Table2 } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { fmtDateTime, shortId } from '../../SheetsManager/parts/SheetImportDetailsModal';
import { LeadOrigin, OriginRef, attrLabel, channelLabel, fetchSheets } from './leadOrigin';

export interface OriginLead {
  sheet_id?: string | null;
  sheet_import_id?: string | null;
  origin?: LeadOrigin | null;
  custom_fields?: Record<string, string> | null;
}

// Attributes shown in their own rows (not repeated in "More details")
const SHOWN_ATTRS = new Set(['ad_name', 'adset_name']);

const refFilterValue = (r: OriginRef) => (r.id ? `id:${r.id}` : `name:${r.name}`);

const Row: React.FC<{
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
  onOpen?: () => void;
  openTitle?: string;
}> = ({ icon: Icon, label, children, onOpen, openTitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', padding: '7.4px 0', borderBottom: `1px solid ${G.soft}` }}>
    <div style={{ width: 26, height: 26, background: C.limeDim, borderRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={12} color={C.teal} />
    </div>
    <span style={{ fontSize: '0.64rem', color: C.muted, fontWeight: 600, width: '74px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
    <div style={{ flex: 1, minWidth: 0, fontSize: '0.74rem', color: '#1f2937', fontWeight: 600 }}>{children}</div>
    {onOpen && (
      <button
        onClick={onOpen}
        title={openTitle}
        aria-label={openTitle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '6px',
          border: `1px solid ${G.line}`,
          background: G.shellBg,
          color: C.teal,
          cursor: 'pointer',
          flexShrink: 0,
          padding: 0,
        }}
      >
        <ArrowRight size={12} />
      </button>
    )}
  </div>
);

const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: '0.6rem', color: C.muted, fontWeight: 500, fontFamily: 'monospace', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
    {children}
  </div>
);

const KeyValues: React.FC<{ title: string; entries: [string, string][] }> = ({ title, entries }) =>
  entries.length === 0 ? null : (
    <div style={{ marginTop: '10px' }}>
      <div style={{ fontSize: '0.6rem', fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(80px, auto) 1fr', gap: '4px 10px', padding: '8px 10px', background: G.shellBg, border: `1px solid ${G.line}`, borderRadius: '8px' }}>
        {entries.map(([k, v]) => (
          <React.Fragment key={k}>
            <span style={{ fontSize: '0.66rem', color: C.muted, textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k}>
              {k}
            </span>
            <span style={{ fontSize: '0.7rem', color: '#1f2937', fontWeight: 600, wordBreak: 'break-word' }}>{v}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );

const LeadOriginCard: React.FC<{ lead: OriginLead }> = ({ lead }) => {
  const navigate = useNavigate();
  const o = lead.origin;
  const [sheetName, setSheetName] = useState<string | null>(null);

  // Sheet label = its current display name (leads only store the ID)
  useEffect(() => {
    if (!lead.sheet_id) return;
    let alive = true;
    fetchSheets()
      .then((list) => {
        if (alive) setSheetName(list.find((s) => s.sheet_id === lead.sheet_id)?.display_name || null);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [lead.sheet_id]);

  const attrs = Object.entries(o?.attributes ?? {})
    .filter(([k]) => !SHOWN_ATTRS.has(k))
    .map(([k, v]) => [attrLabel(k), v] as [string, string]);
  const custom = Object.entries(lead.custom_fields ?? {});

  const hasAnything = !!(o?.campaign || o?.form || o?.submitted_at || o?.external_id || lead.sheet_id || attrs.length || custom.length);
  if (!hasAnything) return null;

  const adName = o?.attributes?.ad_name;
  const adsetName = o?.attributes?.adset_name;

  return (
    <div style={{ background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' }}>
      <div style={{ background: C.white, border: `1px solid ${G.line}`, borderRadius: '9.2px', padding: '14px 16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '6px',
            paddingBottom: '10px',
            borderBottom: `1px solid ${G.soft}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px' }}>
            <div
              style={{
                width: 29.5,
                height: 29.5,
                borderRadius: '7.4px',
                background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.lime,
              }}
            >
              <Compass size={14} />
            </div>
            <span style={{ color: C.teal, fontWeight: 700, fontSize: '0.71rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Where it came from</span>
          </div>
          {o?.channel && (
            <span
              style={{
                padding: '2.8px 8.3px',
                borderRadius: '20px',
                fontSize: '0.6rem',
                fontWeight: 700,
                background: 'rgba(59,130,246,0.1)',
                color: '#1d4ed8',
                border: '1px solid rgba(59,130,246,0.3)',
                whiteSpace: 'nowrap',
              }}
            >
              {channelLabel(o.channel)}
            </span>
          )}
        </div>

        {o?.campaign && (o.campaign.id || o.campaign.name) && (
          <Row
            icon={Megaphone}
            label="Campaign"
            onOpen={() => navigate(`/leads?campaign=${encodeURIComponent(refFilterValue(o.campaign!))}`)}
            openTitle="All leads from this campaign"
          >
            {o.campaign.name || o.campaign.id}
            {o.campaign.id && o.campaign.name && <Sub>{o.campaign.id}</Sub>}
          </Row>
        )}
        {(adName || adsetName) && (
          <Row icon={Megaphone} label="Ad">
            {adName || '—'}
            {adsetName && <Sub>Ad set: {adsetName}</Sub>}
          </Row>
        )}
        {o?.form && (o.form.id || o.form.name) && (
          <Row
            icon={ClipboardList}
            label="Form"
            onOpen={() => navigate(`/leads?form=${encodeURIComponent(refFilterValue(o.form!))}`)}
            openTitle="All leads from this form"
          >
            {o.form.name || o.form.id}
            {o.form.id && o.form.name && <Sub>{o.form.id}</Sub>}
          </Row>
        )}
        {o?.submitted_at && (
          <Row icon={Calendar} label="Submitted">
            {fmtDateTime(o.submitted_at)}
          </Row>
        )}
        {lead.sheet_id && (
          <Row
            icon={Table2}
            label="Sheet"
            onOpen={() => navigate(`/leads?sheet_id=${encodeURIComponent(lead.sheet_id!)}`)}
            openTitle="All leads from this sheet"
          >
            {sheetName || <span style={{ fontFamily: 'monospace' }}>{shortId(lead.sheet_id)}</span>}
            {sheetName && <Sub>{shortId(lead.sheet_id)}</Sub>}
          </Row>
        )}
        {o?.external_id && (
          <Row icon={ListPlus} label="Lead ID">
            <span style={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>{o.external_id}</span>
          </Row>
        )}

        <KeyValues title="More details" entries={attrs} />
        <KeyValues title="Custom fields" entries={custom} />
      </div>
    </div>
  );
};

export default LeadOriginCard;
